import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export type PendingUnpack = {
  listId:    string
  startedAt: string
  itemCount: number
} | null

export function usePendingUnpack(householdId: string | null): {
  pending: PendingUnpack
  loading: boolean
  refresh: () => void
} {
  const [pending, setPending] = useState<PendingUnpack>(null)
  const [loading, setLoading] = useState(true)
  const [nonce,   setNonce]   = useState(0)

  useEffect(() => {
    if (!householdId) { setLoading(false); return }

    let cancelled = false
    setLoading(true)

    ;(async () => {
      // shopping_lists has no status column — detect "needs unpacking" by the
      // presence of checked items on a list whose unpack hasn't been finalized.
      // unpacked_at is the dedicated marker (set by useFinishUnpack on success);
      // pre-migration rows where unpacked_at is null but completed_at is set
      // were backfilled, so this filter alone is sufficient.
      const { data: activeLists } = await supabase
        .from('shopping_lists')
        .select('id, generated_at')
        .eq('household_id', householdId)
        .is('unpacked_at', null)
        .order('generated_at', { ascending: false })
        .limit(3)

      if (cancelled) return

      if (!activeLists?.length) { setPending(null); setLoading(false); return }

      const listIds = activeLists.map(l => l.id)

      const { data: checkedItem } = await supabase
        .from('list_items')
        .select('list_id')
        .in('list_id', listIds)
        .eq('status', 'checked')
        .limit(1)
        .maybeSingle()

      if (cancelled) return

      if (!checkedItem) { setPending(null); setLoading(false); return }

      const { count } = await supabase
        .from('list_items')
        .select('id', { count: 'exact', head: true })
        .eq('list_id', checkedItem.list_id)
        .neq('status', 'removed')

      if (cancelled) return

      const parentList = activeLists.find(l => l.id === checkedItem.list_id)

      setPending({
        listId:    checkedItem.list_id,
        startedAt: parentList?.generated_at ?? new Date().toISOString(),
        itemCount: count ?? 0,
      })
      setLoading(false)
    })()

    return () => { cancelled = true }
  }, [householdId, nonce])

  return { pending, loading, refresh: () => setNonce(n => n + 1) }
}
